import { useCallback, useState } from 'react';

import { useRecaptchaContext } from '../contexts/RecaptchaContext';

interface UseRecaptchaResult {
  verify: (action?: string) => Promise<string>;
  isLoading: boolean;
  error: Error | null;
  isEnabled: boolean;
}

/**
 * Custom hook to easily use reCAPTCHA in components
 */
export const useRecaptcha = (): UseRecaptchaResult => {
  const { executeRecaptcha, isEnabled } = useRecaptchaContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const verify = useCallback(
    async (action: string = 'submit'): Promise<string> => {
      if (!isEnabled) {
        return '';
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = await executeRecaptcha(action);
        return token;
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error('Unknown reCAPTCHA error');
        setError(errorObj);
        return '';
      } finally {
        setIsLoading(false);
      }
    },
    [executeRecaptcha, isEnabled]
  );

  return {
    verify,
    isLoading,
    error,
    isEnabled,
  };
};

export default useRecaptcha;
